import { SortOrder } from '@/enums/SortOrder';
import qs from 'qs';
import { Module, ActionTree, MutationTree, GetterTree } from 'vuex';

const initialState: ProductTranslationState = {
  all: [],
  viewing: undefined,
};

const getters: GetterTree<ProductTranslationState, RootState> = {
  all(state: ProductTranslationState): ProductTranslation[] {
    return state.all;
  },
};

const mutations: MutationTree<ProductTranslationState> = {
  RESET(state: ProductTranslationState) {
    state.all = [];
  },
  ADD_MODEL(state: ProductTranslationState, payload: ProductTranslation) {
    state.all.push(payload);
  },
  ADD_MODELS(state: ProductTranslationState, payload: ProductTranslation[]) {
    state.all = [...state.all, ...payload];
  },
  SET_MODELS(state: ProductTranslationState, payload: ProductTranslation[]) {
    state.all = payload;
  },
  UPDATE_MODEL(state: ProductTranslationState, payload: ProductTranslation) {
    const index = state.all.findIndex((model: ProductTranslation) => model.id === payload.id);

    if (index === -1) {
      state.all.push(payload);
    } else {
      state.all[index] = payload;
    }
  },
  REMOVE_MODEL(state: ProductTranslationState, payload: number) {
    state.all = state.all.filter((model: ProductTranslation) => model.id !== payload);
  },
};

const resource: string = 'product-translations';

const actions: ActionTree<ProductTranslationState, RootState> = {
  clearViewing({ commit }) {
    commit('SET_VIEWING', undefined);
  },
  create({ commit, rootState }, { data, workspaceId }: { data: ProductTranslationData; workspaceId: number }) {
    return rootState.api
      .post(`${resource}`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: ProductTranslation }) => {
        commit('ADD_MODEL', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  index(
    { commit, rootState },
    { query = { s: { ordinal: SortOrder.ASC, created_at: SortOrder.DESC, id: SortOrder.ASC }, per_page: 20 }, workspaceId }: { query?: IndexQuery<ProductTranslation>; workspaceId: number },
  ) {
    const queryString = qs.stringify(query, { encode: false });

    return rootState.api
      .get(`${resource}?${queryString}`, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: ProductTranslation }) => {
        if (query.hasOwnProperty('after')) {
          commit('ADD_MODELS', response.data);
        } else {
          commit('SET_MODELS', response.data);
        }
        return Promise.resolve(response);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  read({ commit, rootState }, { id, workspaceId }: { id: number; workspaceId: number }) {
    return rootState.api
      .get(`${resource}/${id}`, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: ProductTranslation }) => {
        commit('UPDATE_MODEL', response.data);

        commit('SET_VIEWING', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  update({ commit, rootState }, { id, data, workspaceId }: { id: number; data: ProductTranslationData; workspaceId: number }) {
    return rootState.api
      .put(`${resource}/${id}`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: ProductTranslation }) => {
        commit('UPDATE_MODEL', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  patch({ commit, rootState }, { id, data, workspaceId }: { id: number; data: Partial<ProductTranslationData>; workspaceId: number }) {
    return rootState.api
      .patch(`${resource}/${id}`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: ProductTranslation }) => {
        commit('UPDATE_MODEL', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  delete({ commit, rootState }, { id, workspaceId }: { id: number; workspaceId: number }) {
    return rootState.api
      .delete(`${resource}/${id}`, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: ProductTranslation }) => {
        commit('REMOVE_MODEL', id);

        return Promise.resolve(id);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  reset({ commit, rootState }) {
    commit('RESET');
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
